import { AzureLogo, GCPLogo, M365Logo } from 'assets';
import IconButton from 'components/IconButton';
import { useAtom, useAtomValue } from 'jotai';
import React, { useMemo } from 'react';
import Select from 'react-select';
import { Col } from 'reactstrap';

import { BPA_SEVERITY_CONFIG, sortBPAProviders } from './config';
import {
  colorStyles,
  SeverityOption,
} from '../../components/SeverityFilterDropdown/config';
import dashboardStyles from '../../styles.module.css';
import {
  appliedIntegrationIdsAtom,
  dashboardSourceDataLoadableAtom,
  providerFiltersAtom,
  severityFiltersAtom,
} from '../state';

const ALL_SEVERITIES = BPA_SEVERITY_CONFIG.map((option) => option.value);
const ALL_SEVERITY_OPTION: SeverityOption = {
  value: 'all',
  label: 'All',
  color: '#055193',
};

const SeverityFilter = () => {
  const [selectedValues, setSelectedValues] = useAtom(severityFiltersAtom);
  const severityOptions: SeverityOption[] = BPA_SEVERITY_CONFIG.map(
    (option) => ({
      ...option,
      value: String(option.value),
    }),
  );
  const options = [ALL_SEVERITY_OPTION, ...severityOptions];
  const allSelected = ALL_SEVERITIES.every((severity) =>
    selectedValues.includes(severity),
  );
  const selectedOptions = severityOptions.filter(
    (option) => allSelected || selectedValues.includes(Number(option.value)),
  );

  const handleChange = (items: readonly SeverityOption[]) => {
    const hasAll = items.some((option) => option.value === 'all');
    const severities = items
      .filter((option) => option.value !== 'all')
      .map((option) => Number(option.value));

    if (hasAll && !allSelected) {
      setSelectedValues([...ALL_SEVERITIES]);
      return;
    }
    if (hasAll && severities.length > 0) {
      setSelectedValues(severities);
      return;
    }
    setSelectedValues(
      severities.length === ALL_SEVERITIES.length
        ? [...ALL_SEVERITIES]
        : severities,
    );
  };

  return (
    <Select<SeverityOption, true>
      className={`custom-360-dropdown ${dashboardStyles.mw250} px-2`}
      options={options}
      isMulti
      value={selectedOptions}
      onChange={handleChange}
      hideSelectedOptions
      closeMenuOnSelect={false}
      styles={colorStyles}
      placeholder="Severity"
      aria-label="BPA severity"
    />
  );
};

const PROVIDER_LOGOS: Record<string, string> = {
  AZURE: AzureLogo as string,
  GCP: GCPLogo as string,
  M365: M365Logo as string,
};

const ProviderIcon = ({ provider }: { provider: string }) => {
  const normalizedProvider = provider.toUpperCase();
  if (normalizedProvider === 'AWS') {
    return (
      <span
        className="fa-stack-lg"
        style={{ marginRight: '5px' }}
        aria-label={provider}
      >
        <i className="fa-brands fa-aws fa-lg"></i>
      </span>
    );
  }

  const logo = PROVIDER_LOGOS[normalizedProvider];
  return logo ? (
    <img width={22} height={22} src={logo} alt={provider} />
  ) : (
    <i className="fa fa-cloud fa-lg" aria-label={provider}></i>
  );
};

const ProviderFilters = () => {
  const [selectedValues, setSelectedValues] = useAtom(providerFiltersAtom);
  const dashboardData = useAtomValue(dashboardSourceDataLoadableAtom);
  const options = useMemo(() => {
    const providers =
      dashboardData.state === 'hasData'
        ? dashboardData.data?.filter_data?.providers || []
        : [];
    return sortBPAProviders(providers).map((provider) => ({
      value: provider.provider,
      label: provider.name,
    }));
  }, [dashboardData]);
  const selectedProvider =
    selectedValues.length === 1 ? selectedValues[0] : undefined;

  return (
    <div className="d-flex align-items-center" aria-label="BPA providers">
      {options.map((option) => (
        <IconButton
          key={option.value}
          id={`bpaProvider${option.value}`}
          iconElement={<ProviderIcon provider={option.value} />}
          tooltip={
            selectedProvider === option.value
              ? 'Show All Providers'
              : `${option.label} Only`
          }
          active={selectedProvider === option.value}
          onClick={() =>
            setSelectedValues(
              selectedProvider === option.value ? [] : [option.value],
            )
          }
        />
      ))}
    </div>
  );
};

const AppliedIntegrationInfo = () => {
  const [integrationIds, setIntegrationIds] = useAtom(
    appliedIntegrationIdsAtom,
  );
  if (integrationIds.length === 0) return null;

  return (
    <div className="d-flex align-items-center">
      <i className="fa fa-info-circle me-1 text-warning px-2"></i>
      Dashboard scoped to{' '}
      <strong className="px-1">{integrationIds.length}</strong> integrations.
      <IconButton
        id="resetBpaDashboardIntegrations"
        iconClass="fa fa-undo fa-lg"
        tooltip="Reset Dashboard Charts"
        onClick={() => setIntegrationIds([])}
      />
    </div>
  );
};

const DashboardFilters = () => (
  <nav className="navbar navbar-expand-sm bg-white border-top-0 border border-bottom-light sticky-top px-0">
    <div className="row w-100 align-items-center m-0">
      <Col xs="12" md="4" lg="5">
        <AppliedIntegrationInfo />
      </Col>
      <Col
        xs="12"
        md="8"
        lg="7"
        className="d-flex flex-wrap justify-content-end align-items-center"
      >
        <SeverityFilter />
        <ProviderFilters />
      </Col>
    </div>
  </nav>
);

export default DashboardFilters;
